import { type ReactElement } from 'react';
import { redirect, useLoaderData } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { type PayoutStatus } from '@marketplace/contracts';
import { localeOf } from '../features/auth/actions.js';
import { PayoutsAccount } from '../features/payouts/PayoutsAccount.js';
import {
  fromOf,
  loadMyPayouts,
  startOnboarding,
  type OnboardingFailure,
} from '../features/payouts/onboarding.js';
import { loadSession } from '../shared/session.js';

/**
 * `/:lang/account/payouts/refresh?from=<from>` — Stripe's `refresh_url`. `W5-T01` §4.3.
 *
 * Stripe sends the provider here when a link has expired or was already used, and says what to do:
 * *"call the Account Links API again with the same parameters"*. So the page shows nothing. It asks
 * for a fresh link and redirects to it. Only when that fails does it render: the account page, with
 * the error line.
 *
 * `from` comes from a URL anybody can type, so it is read against the closed set and anything else is
 * the account page's. It is never a URL: the return and refresh URLs are the server's (§4.2).
 */
interface RefreshData {
  locale: string;
  payouts: PayoutStatus | null;
  failure: OnboardingFailure;
}

export async function loader({
  params,
  request,
  context,
}: LoaderFunctionArgs): Promise<RefreshData> {
  const locale = localeOf(params);

  const session = await loadSession(context);
  if (session === null) throw redirect(`/${locale}/login`);

  const from = fromOf(new URL(request.url).searchParams.get('from'));
  const outcome = await startOnboarding(context, locale, from);
  if (outcome instanceof Response) throw outcome;

  return {
    locale,
    payouts: await loadMyPayouts(context, { fresh: true }),
    failure: outcome.failure,
  };
}

export function Component(): ReactElement {
  const { locale, payouts, failure } = useLoaderData<RefreshData>();
  return <PayoutsAccount locale={locale} payouts={payouts} failure={failure} />;
}
